import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { api, SitePromoModalPayload } from '../../services/api';

const STORAGE_PREFIX = 'racecarly-promo-dismissed-';
const BBORR_SITE_URL = 'https://bborr.com/';

function isActivePromo(data: SitePromoModalPayload): data is Extract<SitePromoModalPayload, { active: true }> {
  return data.active === true;
}

export const SitePromoModal: React.FC = () => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [, bumpStorageRead] = useState(0);

  const { data, isSuccess } = useQuery({
    queryKey: ['site-promo-modal'],
    queryFn: () => api.products.getPromoModal(),
    staleTime: 60_000,
  });

  const promo = isSuccess && data && isActivePromo(data) ? data : null;

  const dismissed =
    !promo ||
    (typeof localStorage !== 'undefined' &&
      localStorage.getItem(`${STORAGE_PREFIX}${promo.campaignKey}`) === '1');

  const visible = !!promo && !dismissed;

  const dismiss = useCallback(() => {
    if (!promo) return;
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${promo.campaignKey}`, '1');
    } catch {
      /* ignore */
    }
    bumpStorageRead(t => t + 1);
  }, [promo]);

  useEffect(() => {
    if (!visible || !promo) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [visible, promo, dismiss]);

  if (!promo || !visible) return null;

  return (
    <motion.div
      key="site-promo"
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <motion.div
        className="absolute inset-0 cursor-pointer"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(180, 40, 40, 0.35) 0%, rgba(10, 20, 80, 0.55) 45%, rgba(0, 0, 0, 0.82) 100%)',
        }}
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={dismiss}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-promo-title"
        className="relative w-full max-w-lg sm:max-w-md max-h-[92vh] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl border border-white/15"
        style={{
          background: 'linear-gradient(145deg, rgba(30, 18, 18, 0.97) 0%, rgba(12, 14, 28, 0.98) 100%)',
          boxShadow:
            '0 0 0 1px rgba(255, 215, 120, 0.12), 0 25px 80px rgba(0, 0, 0, 0.65), 0 0 60px rgba(180, 40, 60, 0.15)',
        }}
        initial={{ opacity: 0, y: 48, scale: 0.94 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { type: 'spring', stiffness: 320, damping: 28 },
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(-12deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 3px)',
          }}
          aria-hidden
        />
        <div className="relative flex items-start justify-between gap-3 px-4 pt-4 pb-2 border-b border-white/10">
          <h2
            id="site-promo-title"
            className="text-left font-semibold tracking-wide text-[#f5e6d3]"
            style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.35rem', letterSpacing: '0.08em' }}
          >
            {promo.title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={dismiss}
            className="shrink-0 rounded-lg p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#c9a84c] hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
            aria-label="Close announcement"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="relative flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 pt-3">
          <p className="sr-only">Select the poster below to open the official BBORR website in a new tab.</p>
          <motion.div
            className="rounded-xl overflow-hidden ring-1 ring-[#8b2942]/50"
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.08, duration: 0.4 }}
          >
            <a
              href={BBORR_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] rounded-xl"
              aria-label={`${promo.title} — opens bborr.com in a new tab`}
              onClick={() => dismiss()}
            >
              <img
                src={promo.imageUrl}
                alt=""
                className="w-full h-auto max-h-[min(70vh,520px)] object-contain object-top bg-black/40"
                loading="eager"
                decoding="async"
              />
            </a>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};
